import { useMemo } from "react";
import type { Trip } from "../../core/index.ts";

/**
 * One decoded point of a trip's recorded route.
 *
 * Wire format is `"lat,lng,epochSeconds,speedKmh"` (Locale.US decimals), shared
 * with Android's `RoutePoint` and iOS's `RoutePoint`. Decoding is deliberately
 * tolerant, matching them: a legacy 2-part `"lat,lng"` point is valid with
 * `ts`/`speed` defaulting to 0, extra fields are ignored, and an unparseable
 * point is dropped rather than failing the whole trip.
 */
export interface RoutePoint {
  lat: number;
  lng: number;
  ts: number;
  speedKmh: number;
}

export function decodeRoutePoint(raw: string): RoutePoint | null {
  const parts = raw.split(",");
  if (parts.length < 2) return null;
  const lat = Number(parts[0]);
  const lng = Number(parts[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const ts = parts.length > 2 ? Number(parts[2]) : 0;
  const speedKmh = parts.length > 3 ? Number(parts[3]) : 0;
  return {
    lat,
    lng,
    ts: Number.isFinite(ts) ? ts : 0,
    speedKmh: Number.isFinite(speedKmh) ? speedKmh : 0,
  };
}

export function decodeRoute(polyline: string[] | undefined): RoutePoint[] {
  return (polyline ?? []).map(decodeRoutePoint).filter((p): p is RoutePoint => p !== null);
}

// ── Web Mercator ────────────────────────────────────────────────────────────
// Same projection Android's StaticMap uses, so the same trip lines up with the
// same streets on both platforms.

const TILE = 256;

function project(lat: number, lng: number, zoom: number) {
  const scale = TILE * Math.pow(2, zoom);
  const sinLat = Math.sin((lat * Math.PI) / 180);
  return {
    x: ((lng + 180) / 360) * scale,
    // Clamped: at the poles the log term runs away to infinity.
    y:
      (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) *
      scale,
  };
}

/**
 * Largest zoom at which the whole route still fits the viewport, capped so a
 * near-stationary "route" doesn't zoom to individual buildings.
 */
function fitZoom(points: RoutePoint[], width: number, height: number): number {
  const lats = points.map((p) => p.lat);
  const lngs = points.map((p) => p.lng);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);

  for (let zoom = 17; zoom >= 1; zoom--) {
    const a = project(minLat, minLng, zoom);
    const b = project(maxLat, maxLng, zoom);
    const spanX = Math.abs(b.x - a.x);
    const spanY = Math.abs(b.y - a.y);
    // 1.15 headroom so the path never touches the frame — matches Android's pad.
    if (spanX * 1.15 <= width && spanY * 1.15 <= height) return zoom;
  }
  return 1;
}

/**
 * A trip's recorded route drawn over OpenStreetMap raster tiles.
 *
 * Deliberately dependency-free: the tiles are plain `<img>` elements positioned
 * on a Mercator grid and the path is one SVG polyline over them, so the web app
 * stays a static client-only bundle with no map SDK and no API key — the same
 * trade Android makes with its stitched static tiles.
 *
 * Tiles come from OpenStreetMap's standard style (keyless) and are inverted +
 * hue-rotated in CSS to match the app's dark theme — OSM has no dark style and
 * CARTO's dark basemap now watermarks keyless requests. Attribution is required
 * and is rendered in the corner.
 */
export function TripRouteMap({
  points,
  width = 640,
  height = 260,
}: {
  points: RoutePoint[];
  width?: number;
  height?: number;
}) {
  const layout = useMemo(() => {
    if (points.length < 2) return null;

    const zoom = fitZoom(points, width, height);
    const projected = points.map((p) => project(p.lat, p.lng, zoom));
    const xs = projected.map((p) => p.x);
    const ys = projected.map((p) => p.y);
    const centreX = (Math.min(...xs) + Math.max(...xs)) / 2;
    const centreY = (Math.min(...ys) + Math.max(...ys)) / 2;

    // World pixel of the viewport's top-left corner.
    const originX = centreX - width / 2;
    const originY = centreY - height / 2;

    const tiles: { key: string; url: string; left: number; top: number }[] = [];
    const maxTile = Math.pow(2, zoom);
    const firstTileX = Math.floor(originX / TILE);
    const firstTileY = Math.floor(originY / TILE);
    const lastTileX = Math.floor((originX + width) / TILE);
    const lastTileY = Math.floor((originY + height) / TILE);

    for (let tx = firstTileX; tx <= lastTileX; tx++) {
      for (let ty = firstTileY; ty <= lastTileY; ty++) {
        // Wrap horizontally at the antimeridian; skip out-of-range rows, which
        // have no tile at all rather than a wrapped one.
        if (ty < 0 || ty >= maxTile) continue;
        const wrappedX = ((tx % maxTile) + maxTile) % maxTile;
        tiles.push({
          key: `${zoom}/${tx}/${ty}`,
          url: `https://tile.openstreetmap.org/${zoom}/${wrappedX}/${ty}.png`,
          left: tx * TILE - originX,
          top: ty * TILE - originY,
        });
      }
    }

    const path = projected.map((p) => `${p.x - originX},${p.y - originY}`).join(" ");
    const first = projected[0]!;
    const last = projected[projected.length - 1]!;

    return {
      tiles,
      path,
      start: { x: first.x - originX, y: first.y - originY },
      end: { x: last.x - originX, y: last.y - originY },
    };
  }, [points, width, height]);

  if (!layout) return null;

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        maxWidth: width,
        height,
        overflow: "hidden",
        borderRadius: 14,
        background: "#17181b",
      }}
    >
      {layout.tiles.map((tile) => (
        <img
          key={tile.key}
          src={tile.url}
          alt=""
          loading="lazy"
          width={TILE}
          height={TILE}
          style={{
            position: "absolute",
            left: tile.left,
            top: tile.top,
            // Turns the light OSM style into a dark one; same transform as the
            // Android map's colour matrix, so both platforms look alike.
            filter: "invert(1) hue-rotate(180deg)",
          }}
        />
      ))}

      <svg
        width={width}
        height={height}
        style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
      >
        <polyline
          points={layout.path}
          fill="none"
          stroke="#ffffff"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx={layout.start.x} cy={layout.start.y} r={5} fill="#f0a500" />
        <circle cx={layout.end.x} cy={layout.end.y} r={5} fill="#e5484d" />
      </svg>

      <div
        className="mono"
        style={{
          position: "absolute",
          right: 6,
          bottom: 4,
          fontSize: 9,
          color: "#9aa0a6",
          textShadow: "0 0 3px #000",
        }}
      >
        © OpenStreetMap contributors
      </div>
    </div>
  );
}

/**
 * Route points to draw for a trip: the recorded GPS track when there is one.
 *
 * Unlike Android and iOS this does *not* fall back to geocoding the trip's stop
 * names — the web app is offline-first and client-only, and a geocoder call per
 * row would mean a network dependency and a third-party rate limit on a page
 * that otherwise works with no server at all. Hand-logged trips show their stop
 * names as text instead.
 */
export function tripRoutePoints(trip: Trip): RoutePoint[] {
  return decodeRoute(trip.polyline);
}
